import { useEffect, useState, useMemo, useRef } from "react";
import api, { openPdf } from "../lib/api";
import Pagination from "../components/Pagination";
import { downloadCsv } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, TruckIcon, Download, Search, CheckSquare, Square, UserPlus, X, Pencil, Package, CalendarClock, ChevronLeft, ChevronRight, List, CalendarDays, Mail, FileText, Boxes, MapPin, ChevronDown, ChevronUp, PackageCheck, Clock, ScanLine, Camera, Sparkles } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { beep, normSku } from "../lib/scan";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import { useAuth } from "../context/AuthContext";
import CustomerAutocomplete from "../components/CustomerAutocomplete";
import ItemAutocomplete from "../components/ItemAutocomplete";
import OrderImportDialog from "../components/OrderImportDialog";
import { lookupPlz } from "../lib/plz";

const CAL_WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const cpad = (n) => String(n).padStart(2, "0");
const cymd = (d) => `${d.getFullYear()}-${cpad(d.getMonth() + 1)}-${cpad(d.getDate())}`;
const ORDER_STATUS_COLOR = { draft: "#9ca3af", confirmed: "#3b82f6", in_production: "#d97706", ready: "#10b981", cancelled: "#ef4444" };
const statusColor = (s) => ORDER_STATUS_COLOR[s] || "#EB75B5";
const round2 = (x) => Math.round(Number((x * 100).toPrecision(12))) / 100;
function calMonthDays(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const fw = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(1 - fw);
  const out = []; for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(start.getDate() + i); out.push(d); } return out;
}
function calWeekDays(anchor) {
  const d = new Date(anchor); const dow = (d.getDay() + 6) % 7;
  const mon = new Date(d); mon.setDate(d.getDate() - dow);
  const out = []; for (let i = 0; i < 7; i++) { const x = new Date(mon); x.setDate(mon.getDate() + i); out.push(x); } return out;
}

function OrdersCalendar({ orders, loading, mode, anchor, setAnchor, onOpen, fmt }) {
  const byDay = useMemo(() => {
    const m = {};
    for (const o of orders) { const k = (o.expected_delivery || "").slice(0, 10); if (!k) continue; (m[k] = m[k] || []).push(o); }
    return m;
  }, [orders]);
  const todayKey = cymd(new Date());
  const move = (delta) => { const d = new Date(anchor); if (mode === "month") d.setMonth(d.getMonth() + delta); else d.setDate(d.getDate() + delta * 7); setAnchor(d); };
  const label = mode === "month"
    ? anchor.toLocaleDateString("de-DE", { month: "long", year: "numeric" })
    : (() => { const w = calWeekDays(anchor); return `${w[0].toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${w[6].toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`; })();
  const days = mode === "month" ? calMonthDays(anchor) : calWeekDays(anchor);

  const Chip = (o) => (
    <button key={o.id} data-testid={`cal-order-${o.number}`} onClick={() => onOpen(o.id)}
            title={`${o.customer_name || "—"} · ${o.number} · Netto ${fmt(o.total_net)}`}
            className="w-full truncate rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white cursor-pointer hover:opacity-90 text-left"
            style={{ background: statusColor(o.status) }}>
      {o.customer_name || o.number}
    </button>
  );

  return (
    <div className="p-3" data-testid="orders-calendar">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="font-display font-bold text-lg capitalize text-[var(--text)]">{label}</div>
        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="h-8 w-8 rounded-full border border-[var(--border)] flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5"><ChevronLeft size={16} /></button>
          <button data-testid="cal-today" onClick={() => setAnchor(new Date())} className="h-8 px-3 rounded-full border border-[var(--border)] text-xs font-semibold hover:bg-black/5 dark:hover:bg-white/5">Heute</button>
          <button data-testid="cal-next" onClick={() => move(1)} className="h-8 w-8 rounded-full border border-[var(--border)] flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5"><ChevronRight size={16} /></button>
        </div>
      </div>
      {loading && <div className="text-center py-10 text-sm text-[var(--text-muted)]">Lade Termine…</div>}
      {!loading && mode === "month" && (
        <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {CAL_WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)] py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const k = cymd(d); const inMonth = d.getMonth() === anchor.getMonth(); const isToday = k === todayKey; const list = byDay[k] || [];
              return (
                <div key={k} data-testid={`cal-day-${k}`} className={`min-h-[92px] rounded-xl p-1.5 border border-[var(--border)] ${inMonth ? "bg-[var(--surface)]" : "bg-black/[0.03] dark:bg-white/[0.03] text-[var(--text-muted)]"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-mono ${isToday ? "bg-[var(--pop-pink)] text-white rounded-full px-1.5 py-0.5" : ""}`}>{d.getDate()}</span>
                    {list.length > 0 && <span className="text-[10px] text-[var(--text-muted)]">{list.length}</span>}
                  </div>
                  <div className="space-y-0.5">
                    {list.slice(0, 4).map(Chip)}
                    {list.length > 4 && <div className="text-[10px] text-[var(--text-muted)] font-mono">+{list.length - 4}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {!loading && mode === "week" && (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-1">
          {days.map((d) => {
            const k = cymd(d); const isToday = k === todayKey; const list = byDay[k] || [];
            return (
              <div key={k} data-testid={`cal-day-${k}`} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-2 min-h-[140px]">
                <div className={`text-xs font-semibold mb-1.5 flex items-center justify-between ${isToday ? "text-[var(--pop-pink)]" : "text-[var(--text-muted)]"}`}>
                  <span>{CAL_WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}.{d.getMonth() + 1}.</span>
                  {list.length > 0 && <span className="text-[10px]">{list.length}</span>}
                </div>
                <div className="space-y-1">
                  {list.map(Chip)}
                  {list.length === 0 && <div className="text-[10px] text-[var(--text-muted)] italic">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && orders.length === 0 && <div className="text-center py-8 text-sm text-[var(--text-muted)]" data-testid="orders-calendar-empty">Keine Bestellungen mit Wunschtermin in diesem Zeitraum.</div>}
    </div>
  );
}

const WH_TODAY = () => new Date().toISOString().slice(0, 10);
function whClassify(o) {
  const ds = o.delivery_status;
  if (ds === "shipped") {
    return (o.shipped_at || "").slice(0, 10) === WH_TODAY() ? "shipped_today" : "shipped";
  }
  if (o.status === "cancelled") return "cancelled";
  if (o.shipping_ready) return "ready";
  if (o.delivery_note_id) return "processing";
  return "new";
}
const WH_LABEL = {
  new: { t: "Neu", c: "#3b82f6" },
  processing: { t: "In Bearbeitung", c: "#d97706" },
  ready: { t: "Versandbereit", c: "#10b981" },
  shipped_today: { t: "Heute versendet", c: "#64748b" },
  shipped: { t: "Versendet", c: "#64748b" },
};
const WH_TILES = [
  { key: "new", label: "Neue Bestellungen", color: "#3b82f6", icon: Package },
  { key: "processing", label: "In Bearbeitung", color: "#d97706", icon: Clock },
  { key: "ready", label: "Versandbereit", color: "#10b981", icon: PackageCheck },
  { key: "shipped_today", label: "Heute versendet", color: "#64748b", icon: TruckIcon },
];
const WH_CARRIERS = ["DHL", "DPD", "DSV", "GLS", "UPS", "Selbstabholung", "Sonstige"];

// Regalplatz "A-01-03" → { gang, regal, ebene, label, lvl }
function parseShelf(loc) {
  if (!loc) return null;
  const parts = String(loc).trim().split(/[-/\s.]+/).filter(Boolean);
  if (!parts.length) return null;
  const gang = parts[0] || "";
  const regal = parts[1] || "";
  const ebene = parts[2] || "";
  const label = ["Gang " + gang, regal && "Regal " + regal, ebene && "Ebene " + ebene].filter(Boolean).join(" · ");
  const lvl = parseInt(ebene, 10);
  return { gang, regal, ebene, label, lvl: isNaN(lvl) ? 999 : lvl };
}
// Sortierung: unterste Ebene zuerst, dann Gang, dann Regal; ohne Regalplatz ans Ende
function shelfSortKey(sh) {
  if (!sh) return [9999, "~~~", "~~~"];
  return [sh.lvl, sh.gang || "~~~", sh.regal || "~~~"];
}

function WarehouseBoard({ orders, loading, statusFilter, setStatusFilter, q, onReload, fmt }) {
  const classified = useMemo(() => orders.map((o) => ({ ...o, _wh: whClassify(o) })), [orders]);
  const counts = useMemo(() => {
    const c = { new: 0, processing: 0, ready: 0, shipped_today: 0 };
    classified.forEach((o) => { if (c[o._wh] != null) c[o._wh]++; });
    return c;
  }, [classified]);
  const visible = useMemo(() => {
    const ql = (q || "").toLowerCase();
    return classified.filter((o) => {
      if (o._wh === "cancelled") return false;
      if (statusFilter === "all") { if (o._wh === "shipped") return false; }
      else if (o._wh !== statusFilter) return false;
      if (ql) return (o.number || "").toLowerCase().includes(ql) || (o.customer_name || "").toLowerCase().includes(ql);
      return true;
    });
  }, [classified, statusFilter, q]);

  const [dayOpen, setDayOpen] = useState(false);
  const daily = useMemo(() => {
    const today = WH_TODAY();
    const agg = {};
    classified.forEach((o) => {
      if (o._wh !== "new" && o._wh !== "processing") return;
      const due = (o.expected_delivery || "").slice(0, 10);
      if (!due || due > today) return; // nur heute/überfällig
      (o.lines || []).forEach((l) => {
        if (!l.item_id) return;
        const key = l.item_sku || l.item_id;
        const e = agg[key] || { name: l.item_name, sku: l.item_sku, unit: l.item_unit, location: l.warehouse_location, qty: 0, orders: new Set() };
        e.qty += Number(l.quantity) || 0;
        e.orders.add(o.number);
        if (!e.location && l.warehouse_location) e.location = l.warehouse_location;
        agg[key] = e;
      });
    });
    const list = Object.values(agg).map((e) => ({ ...e, shelf: parseShelf(e.location), orderCount: e.orders.size }));
    list.sort((a, b) => {
      const ka = shelfSortKey(a.shelf), kb = shelfSortKey(b.shelf);
      for (let n = 0; n < 3; n++) { if (ka[n] < kb[n]) return -1; if (ka[n] > kb[n]) return 1; }
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [classified]);

  return (
    <div className="p-3 sm:p-4" data-testid="orders-warehouse">
      <button type="button" onClick={() => setDayOpen(true)} data-testid="wh-daily-picklist-btn"
              className="w-full mb-4 flex items-center justify-between rounded-2xl border-2 border-[var(--pop-pink)] bg-[var(--pop-pink)]/5 px-4 py-3 hover:bg-[var(--pop-pink)]/10 transition-colors">
        <span className="inline-flex items-center gap-2 font-bold text-[var(--text)]"><Boxes size={18} className="text-[var(--pop-pink)]" /> Gesamtpackliste heute</span>
        <span className="rounded-full bg-[var(--pop-pink)] text-white text-xs font-bold px-3 py-1">{daily.length} Positionen</span>
      </button>

      <Dialog open={dayOpen} onOpenChange={setDayOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wh-daily-picklist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Gesamtpackliste heute</DialogTitle>
          </DialogHeader>
          <Button variant="outline" size="sm" className="rounded-full w-fit" data-testid="wh-daily-print"
                  onClick={() => openPdf("/warehouse/daily-picklist.pdf").catch(() => toast.error("Druck fehlgeschlagen"))}>
            <FileText size={14} className="mr-1" /> Drucken
          </Button>
          <p className="text-sm text-[var(--text-muted)]">Alle offenen Positionen mit Liefertermin heute oder überfällig – nach Regalplatz sortiert (optimale Route).</p>
          {daily.length === 0 ? (
            <div className="text-center py-8 text-sm text-[var(--text-muted)]" data-testid="wh-daily-empty">Keine offenen Positionen für heute.</div>
          ) : (
            <div className="space-y-1.5 mt-2">
              {daily.map((e, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-[var(--border)] p-2.5" data-testid={`wh-daily-item-${i}`}>
                  <div className={`shrink-0 text-xs font-bold rounded-lg px-2 py-1 ${e.shelf ? "bg-black/[0.05] dark:bg-white/[0.06] text-[var(--text)]" : "bg-amber-500/15 text-amber-600"}`}>
                    {e.shelf ? e.shelf.label : "— kein Lagerplatz"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-[var(--text)] truncate">{e.name || e.sku}</div>
                    <div className="text-xs font-mono text-[var(--text-muted)]">{e.sku ? `SKU ${e.sku} · ` : ""}{e.orderCount} Auftrag(e)</div>
                  </div>
                  <div className="font-mono font-bold text-base shrink-0">{e.qty} {e.unit || ""}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
        {WH_TILES.map((t) => {
          const active = statusFilter === t.key;
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" data-testid={`wh-tile-${t.key}`}
                    onClick={() => setStatusFilter(active ? "all" : t.key)}
                    className={`text-left rounded-2xl p-4 border-2 transition-all active:scale-[0.98] ${active ? "shadow-lg" : "border-transparent hover:border-[var(--border)]"}`}
                    style={{ background: active ? t.color : "var(--surface)", borderColor: active ? t.color : undefined }}>
              <div className="flex items-center justify-between">
                <Icon size={22} style={{ color: active ? "#fff" : t.color }} />
                <span className="text-3xl font-black tabular-nums" style={{ color: active ? "#fff" : "var(--text)" }}>{counts[t.key]}</span>
              </div>
              <div className={`mt-2 text-sm font-bold ${active ? "text-white" : "text-[var(--text)]"}`}>{t.label}</div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-[var(--text-muted)]">Lade Bestellungen…</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-sm text-[var(--text-muted)]" data-testid="wh-empty">Keine Bestellungen in dieser Ansicht.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {visible.map((o) => <WarehouseCard key={o.id} order={o} onReload={onReload} fmt={fmt} />)}
        </div>
      )}
    </div>
  );
}

function WarehouseCard({ order, onReload, fmt }) {
  const o = order;
  const lineItems = (o.lines || []).filter((l) => l.item_id);
  const storeKey = `popcrn.wh.pack.${o.id}`;
  const [checked, setChecked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storeKey) || "[]")); } catch { return new Set(); }
  });
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [tracking, setTracking] = useState(o.tracking_number || "");
  const [carrier, setCarrier] = useState(o.carrier || "DHL");
  const [podUploaded, setPodUploaded] = useState(!!o.signed_pod_file_id);
  const [podUploading, setPodUploading] = useState(false);
  const podRef = useRef(null);
  const uploadPod = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !o.delivery_note_id) return;
    setPodUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/delivery-notes/${o.delivery_note_id}/signed-pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPodUploaded(true); toast.success("Unterschriebener DSV-Lieferschein hochgeladen");
    } catch (err) { toast.error("Upload fehlgeschlagen"); }
    finally { setPodUploading(false); }
  };
  const [scanActive, setScanActive] = useState(false);
  const [scanVal, setScanVal] = useState("");
  const [camActive, setCamActive] = useState(false);
  const [batchMap, setBatchMap] = useState(null);   // { item_id: [batches FEFO asc] }
  const [flash, setFlash] = useState(null);     // { idx, color }
  const scanRef = useRef(null);
  const scanFnRef = useRef(null);
  const lastCamRef = useRef({ code: null, t: 0 });

  const persist = (set) => { try { localStorage.setItem(storeKey, JSON.stringify([...set])); } catch (_e) {} };
  const toggle = (idx) => {
    if (lineItems[idx]?.delivered) return;
    const s = new Set(checked); s.has(idx) ? s.delete(idx) : s.add(idx); setChecked(s); persist(s);
  };
  const openIdx = lineItems.map((l, i) => (l.delivered ? -1 : i)).filter((i) => i >= 0);
  const checkedOpen = openIdx.filter((i) => checked.has(i));
  const allChecked = openIdx.length > 0 ? openIdx.every((i) => checked.has(i)) : lineItems.length > 0;
  const wh = o._wh;

  const handleScan = (raw) => {
    const code = normSku(raw);
    if (!code) return;
    const idx = lineItems.findIndex((l) => normSku(l.item_sku) === code);
    if (idx === -1) {
      beep("warn"); setFlash({ idx: -1, color: "red" });
      toast.error("Artikel gehört nicht zu dieser Bestellung");
    } else if (lineItems[idx]?.delivered) {
      beep("dup"); setFlash({ idx, color: "yellow" });
      toast("Position bereits geliefert");
    } else if (checked.has(idx)) {
      beep("dup"); setFlash({ idx, color: "yellow" });
      toast("Bereits kommissioniert");
    } else {
      const s = new Set(checked); s.add(idx); setChecked(s); persist(s);
      beep("ok"); setFlash({ idx, color: "green" });
    }
    setTimeout(() => setFlash(null), 700);
  };
  scanFnRef.current = handleScan;

  useEffect(() => { if (scanActive && scanRef.current) scanRef.current.focus(); }, [scanActive]);
  useEffect(() => {
    if (!camActive) return;
    const id = `wh-cam-${o.id}`;
    let inst;
    (async () => {
      try {
        inst = new Html5Qrcode(id);
        await inst.start({ facingMode: "environment" }, { fps: 10, qrbox: 200 },
          (txt) => {
            const now = Date.now();
            if (lastCamRef.current.code === txt && now - lastCamRef.current.t < 1500) return;
            lastCamRef.current = { code: txt, t: now };
            scanFnRef.current && scanFnRef.current(txt);
          }, () => {});
      } catch (_e) { toast.error("Kamera nicht verfügbar"); setCamActive(false); }
    })();
    return () => { try { inst && inst.stop().then(() => inst.clear()).catch(() => {}); } catch (_e) {} };
  }, [camActive]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!expanded || batchMap !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const entries = await Promise.all(lineItems.map(async (l) => {
          try {
            const { data } = await api.get("/batches", { params: { item_id: l.item_id, page_size: 200 } });
            const arr = (Array.isArray(data) ? data : (data.items || [])).filter((b) => (b.current_stock ?? b.quantity ?? 0) > 0);
            arr.sort((a, b) => String(a.mhd || "").localeCompare(String(b.mhd || "")));
            return [l.item_id, arr];
          } catch { return [l.item_id, []]; }
        }));
        if (!cancelled) setBatchMap(Object.fromEntries(entries));
      } catch { if (!cancelled) setBatchMap({}); }
    })();
    return () => { cancelled = true; };
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps
  const lbl = WH_LABEL[wh] || WH_LABEL.new;
  const dueKey = (o.expected_delivery || "").slice(0, 10);
  const overdue = dueKey && dueKey <= WH_TODAY();
  const isShipped = wh === "shipped" || wh === "shipped_today";

  const markReady = async () => {
    setBusy(true);
    try {
      let dnId = o.delivery_note_id;
      if (!dnId) { const { data } = await api.post(`/orders/${o.id}/to-delivery`); dnId = data.delivery_note_id; }
      await api.post(`/delivery-notes/${dnId}/mark-shipping-ready`, { ready: true });
      toast.success(`${o.number} als versandbereit gemeldet`);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); } finally { setBusy(false); }
  };
  const doShip = async () => {
    if (!tracking.trim()) { toast.error("Bitte Trackingnummer eingeben"); return; }
    setBusy(true);
    try {
      let dnId = o.delivery_note_id;
      if (!dnId) { const { data } = await api.post(`/orders/${o.id}/to-delivery`); dnId = data.delivery_note_id; }
      await api.post(`/delivery-notes/${dnId}/finalize-shipment`, { tracking_number: tracking.trim(), carrier });
      toast.success(`${o.number} versendet (${carrier})`);
      try { localStorage.removeItem(storeKey); } catch (_e) {}
      setShipOpen(false); onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Versand fehlgeschlagen"); } finally { setBusy(false); }
  };
  const doPartial = async () => {
    const itemIds = [...new Set(checkedOpen.map((i) => lineItems[i].item_id))];
    if (!itemIds.length) { toast.error("Bitte mindestens eine Position abhaken"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.id}/to-delivery`, { item_ids: itemIds });
      toast.success(`Teil-Lieferschein ${data.delivery_note_number} erzeugt · ${data.remaining_positions} Position(en) offen`);
      const fresh = new Set();
      setChecked(fresh); persist(fresh);
      try { openPdf(`/delivery-notes/${data.delivery_note_id}/pdf`); } catch (_e) {}
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Teil-Lieferschein fehlgeschlagen"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 flex flex-col gap-3" data-testid={`wh-card-${o.number}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm text-[var(--text-muted)]">{o.number}</div>
          <div className="font-display text-xl font-bold text-[var(--text)] truncate">{o.customer_name || "—"}</div>
        </div>
        <span className="shrink-0 rounded-full px-3 py-1 text-xs font-bold text-white" style={{ background: lbl.c }} data-testid={`wh-status-${o.number}`}>{lbl.t}</span>
      </div>

      {dueKey && (
        <div className={`inline-flex items-center gap-1.5 text-sm font-semibold ${overdue && !isShipped ? "text-red-600" : "text-[var(--text-muted)]"}`}>
          <CalendarClock size={15} /> Liefertermin: {dueKey}{overdue && !isShipped ? " · fällig!" : ""}
        </div>
      )}

      <button type="button" onClick={() => setExpanded((v) => !v)} data-testid={`wh-packlist-toggle-${o.number}`}
              className="flex items-center justify-between rounded-xl bg-black/[0.03] dark:bg-white/[0.04] px-3 py-2 text-sm font-semibold text-[var(--text)]">
        <span className="inline-flex items-center gap-2"><Package size={16} className="text-[var(--pop-pink)]" /> Packliste ({lineItems.length}{!isShipped ? ` · ${checkedOpen.length}/${openIdx.length} ✓` : ""})</span>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      <div className="flex flex-wrap gap-1.5" data-testid={`wh-actions-${o.number}`}>
        <button type="button"
                onClick={() => openPdf(`/warehouse/orders/${o.id}/picklist.pdf`).catch(() => toast.error("Packliste konnte nicht gedruckt werden"))}
                title="Packliste dieser Bestellung drucken"
                data-testid={`wh-act-packlist-${o.number}`}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 h-7 text-xs font-semibold text-[var(--text-muted)] hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">
          <Package size={13} /> Packliste
        </button>
        <button type="button" disabled={!o.delivery_note_id}
                onClick={() => openPdf(`/delivery-notes/${o.delivery_note_id}/pdf`).catch(() => toast.error("Lieferschein konnte nicht geöffnet werden"))}
                title={o.delivery_note_id ? "Lieferschein öffnen" : "Noch kein Lieferschein vorhanden"}
                data-testid={`wh-act-ls-${o.number}`}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 h-7 text-xs font-semibold text-[var(--text-muted)] enabled:hover:border-[var(--pop-pink)] enabled:hover:text-[var(--pop-pink)] disabled:opacity-40">
          <TruckIcon size={13} /> Lieferschein
        </button>
        <button type="button" disabled={!o.delivery_note_id || !isShipped}
                onClick={() => openPdf(`/delivery-notes/${o.delivery_note_id}/invoice.pdf`).catch(() => toast.error("Rechnung konnte nicht geöffnet werden"))}
                title={isShipped ? "Rechnung öffnen" : "Rechnung erst nach Versand verfügbar"}
                data-testid={`wh-act-rechnung-${o.number}`}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 h-7 text-xs font-semibold text-[var(--text-muted)] enabled:hover:border-[var(--pop-pink)] enabled:hover:text-[var(--pop-pink)] disabled:opacity-40">
          <FileText size={13} /> Rechnung
        </button>
      </div>

      {expanded && (
        <div className="space-y-1.5" data-testid={`wh-packlist-${o.number}`}>
          {!isShipped && (
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <button type="button" onClick={() => setScanActive((v) => !v)} data-testid={`wh-scan-toggle-${o.number}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-bold border transition-colors ${scanActive ? "bg-green-600 text-white border-green-600" : "border-[var(--border)] text-[var(--text-muted)] hover:border-green-600 hover:text-green-600"}`}>
                <ScanLine size={14} /> {scanActive ? "Scanner aktiv" : "Scanner aktivieren"}
              </button>
              {scanActive && (
                <button type="button" onClick={() => setCamActive((v) => !v)} data-testid={`wh-cam-toggle-${o.number}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-bold border transition-colors ${camActive ? "bg-[var(--pop-pink)] text-white border-[var(--pop-pink)]" : "border-[var(--border)] text-[var(--text-muted)]"}`}>
                  <Camera size={14} /> {camActive ? "Kamera an" : "Kamera"}
                </button>
              )}
            </div>
          )}
          {scanActive && !isShipped && (
            <>
              <Input ref={scanRef} autoFocus value={scanVal} data-testid={`wh-scan-input-${o.number}`}
                     onChange={(e) => setScanVal(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleScan(scanVal); setScanVal(""); } }}
                     onBlur={() => { if (scanActive) setTimeout(() => scanRef.current?.focus(), 60); }}
                     placeholder="Artikel scannen (SKU)…"
                     className={`h-10 ${flash ? (flash.color === "red" ? "border-red-500 ring-2 ring-red-400" : flash.color === "green" ? "border-green-500 ring-2 ring-green-400" : "border-yellow-500 ring-2 ring-yellow-400") : ""}`} />
              {camActive && <div id={`wh-cam-${o.id}`} className="rounded-xl overflow-hidden border border-[var(--border)]" />}
            </>
          )}
          {(() => {
            const rows = lineItems.map((l, i) => {
              const batches = (batchMap && batchMap[l.item_id]) || [];
              const oldest = batches[0] || null;
              const shelfRaw = (oldest && oldest.location) || l.warehouse_location;
              return { l, i, oldest, shelf: parseShelf(shelfRaw) };
            });
            rows.sort((a, b) => {
              const ka = shelfSortKey(a.shelf), kb = shelfSortKey(b.shelf);
              for (let n = 0; n < 3; n++) { if (ka[n] < kb[n]) return -1; if (ka[n] > kb[n]) return 1; }
              return a.i - b.i;
            });
            return rows.map(({ l, i, oldest, shelf }) => (
              <button key={i} type="button" disabled={isShipped || l.delivered} onClick={() => toggle(i)}
                      data-testid={`wh-pack-item-${o.number}-${i}`}
                      className={`w-full flex items-center gap-3 rounded-xl border p-2.5 text-left transition-all ${l.delivered ? "border-[var(--border)] bg-black/[0.04] dark:bg-white/[0.04] opacity-70" : checked.has(i) ? "border-green-500 bg-green-500/10" : "border-[var(--border)]"} ${flash && flash.idx === i ? (flash.color === "green" ? "ring-2 ring-green-400" : "ring-2 ring-yellow-400") : ""}`}>
                {l.delivered
                  ? <TruckIcon size={22} className="text-[var(--text-muted)] shrink-0" />
                  : checked.has(i)
                  ? <CheckSquare size={22} className="text-green-600 shrink-0" />
                  : <Square size={22} className="text-[var(--text-muted)] shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-semibold text-sm text-[var(--text)] truncate">{l.item_name || l.item_sku || "Artikel"}</div>
                    <div className="font-mono text-sm shrink-0">{Number(l.quantity) || 0} {l.item_unit || ""}</div>
                  </div>
                  {l.item_sku && <div className="text-xs font-mono text-[var(--text-muted)]">SKU {l.item_sku}</div>}
                  {l.delivered && <div className="text-xs mt-0.5 inline-flex items-center gap-1 text-green-600 font-semibold" data-testid={`wh-pack-delivered-${o.number}-${i}`}><TruckIcon size={12} /> Geliefert{l.delivery_note_number ? ` · ${l.delivery_note_number}` : ""}</div>}
                  <div className={`text-xs mt-0.5 inline-flex items-center gap-1 ${shelf ? "text-[var(--text)]" : "text-amber-600 font-semibold"}`}>
                    <MapPin size={12} /> {shelf ? shelf.label : "— kein Lagerplatz"}
                  </div>
                  {oldest && (
                    <div className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-1" title="Älteste Charge zuerst (FEFO)">
                      <Clock size={12} /> Charge {oldest.charge_number || "—"} · MHD {(oldest.mhd || "").slice(0, 10) || "—"}
                    </div>
                  )}
                </div>
              </button>
            ));
          })()}
        </div>
      )}

      {!isShipped && (
        <div className="flex flex-col gap-2 mt-auto">
          {wh !== "ready" && (
            <Button onClick={markReady} disabled={busy || !allChecked} data-testid={`wh-mark-ready-${o.number}`}
                    className="w-full h-11 rounded-xl text-base font-bold bg-green-600 hover:bg-green-700 text-white disabled:opacity-40">
              <PackageCheck size={18} className="mr-2" /> {allChecked ? "Versandbereit melden" : `Erst alle Artikel abhaken (${checkedOpen.length}/${openIdx.length})`}
            </Button>
          )}
          {checkedOpen.length > 0 && !allChecked && (
            <Button onClick={doPartial} disabled={busy} data-testid={`wh-partial-delivery-${o.number}`}
                    variant="outline"
                    className="w-full h-11 rounded-xl text-base font-bold border-2 border-[var(--pop-pink)] text-[var(--pop-pink)] hover:bg-[var(--pop-pink)]/10 disabled:opacity-40">
              <FileText size={18} className="mr-2" /> Teil-Lieferschein ({checkedOpen.length} Artikel)
            </Button>
          )}
          {(wh === "ready" || wh === "processing") && !shipOpen && (
            <Button onClick={() => setShipOpen(true)} data-testid={`wh-ship-open-${o.number}`}
                    className="w-full h-11 rounded-xl text-base font-bold bg-[var(--pop-pink)] hover:opacity-90 text-white">
              <TruckIcon size={18} className="mr-2" /> Versenden
            </Button>
          )}
          {wh === "ready" && o.delivery_note_id && (
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => openPdf(`/delivery-notes/${o.delivery_note_id}/pdf`)}
                      data-testid={`wh-print-ls-${o.number}`}
                      className="h-11 rounded-xl font-bold border-[var(--border)]">
                <FileText size={16} className="mr-1" /> Lieferschein drucken
              </Button>
              <Button variant="outline" onClick={() => openPdf(`/delivery-notes/${o.delivery_note_id}/shipping-label.pdf`)}
                      data-testid={`wh-print-label-${o.number}`}
                      className="h-11 rounded-xl font-bold border-[var(--border)]">
                <Package size={16} className="mr-1" /> Label drucken
              </Button>
            </div>
          )}
          {shipOpen && (
            <div className="rounded-xl border border-[var(--border)] p-3 space-y-2" data-testid={`wh-ship-form-${o.number}`}>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Trackingnummer"
                     data-testid={`wh-tracking-${o.number}`} className="h-11 text-base" />
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger data-testid={`wh-carrier-${o.number}`} className="h-11 text-base"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WH_CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              {carrier === "DSV" && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 space-y-2" data-testid={`wh-dsv-pod-${o.number}`}>
                  <input ref={podRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={uploadPod} data-testid={`wh-pod-input-${o.number}`} />
                  <Button variant="outline" onClick={() => podRef.current?.click()} disabled={podUploading}
                          data-testid={`wh-pod-upload-${o.number}`} className="w-full h-10 rounded-lg font-bold">
                    <Camera size={15} className="mr-1" /> {podUploaded ? "Unterschriebener LS ✓ (ersetzen)" : (podUploading ? "Lädt…" : "Unterschriebenen DSV-LS hochladen")}
                  </Button>
                  {podUploaded
                    ? <button type="button" onClick={() => openPdf(`/delivery-notes/${o.delivery_note_id}/signed-pod`)} data-testid={`wh-pod-view-${o.number}`} className="text-xs text-[var(--pop-pink)] underline">Beleg ansehen</button>
                    : <p className="text-[11px] text-amber-700" data-testid={`wh-pod-hint-${o.number}`}>Bitte unterschriebenen DSV-Lieferschein hochladen</p>}
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShipOpen(false)} className="flex-1 h-11 rounded-xl">Abbrechen</Button>
                <Button onClick={doShip} disabled={busy || (carrier === "DSV" && !podUploaded)} data-testid={`wh-ship-confirm-${o.number}`}
                        title={carrier === "DSV" && !podUploaded ? "Bitte unterschriebenen DSV-Lieferschein hochladen" : ""}
                        className="flex-1 h-11 rounded-xl font-bold bg-[var(--pop-pink)] hover:opacity-90 text-white">
                  <TruckIcon size={18} className="mr-2" /> Versand bestätigen
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isShipped && (
        <div className="mt-auto rounded-xl bg-green-500/10 border border-green-500/30 p-3 text-sm" data-testid={`wh-shipped-info-${o.number}`}>
          <div className="font-bold text-green-700 dark:text-green-400 inline-flex items-center gap-1.5"><TruckIcon size={15} /> Versendet{o.carrier ? ` · ${o.carrier}` : ""}</div>
          {o.tracking_number && <div className="font-mono text-xs text-[var(--text-muted)] mt-1 break-all">{o.tracking_number}</div>}
        </div>
      )}
    </div>
  );
}

export default function Orders() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [b2bMap, setB2bMap] = useState({});
  const [selCustType, setSelCustType] = useState(null);
  const [offers, setOffers] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("new") === "1") setOpen(true);
    const cid = sp.get("customer");
    if (cid) {
      setForm((f) => ({ ...f, customer_id: cid }));
      loadCustPrices(cid).then((m) => setCustPrices(m));
    }
    // eslint-disable-next-line
  }, []);
  const [form, setForm] = useState({ customer_id: "", order_date: "", expected_delivery: "", notes: "", is_sample: false, order_discount_pct: "", order_discount_reason: "" });
  const [lines, setLines] = useState([{ item_id: "", quantity: "", price: "" }]);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);   // {id, ...} when editing an existing order
  const [editForm, setEditForm] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editSelCustType, setEditSelCustType] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [display120Default, setDisplay120Default] = useState([]);
  useEffect(() => { api.get("/settings/display-120").then((r) => setDisplay120Default(r.data.rows || [])).catch(() => {}); }, []);
  const buildRows120 = (itemId) => {
    const base = (display120Default && display120Default.length ? display120Default : []).slice(0, 6);
    const ids = [...base];
    const fb = itemId || ids[0] || "";
    while (ids.length < 6) ids.push(fb);
    return ids.slice(0, 6).map((id) => ({ item_id: id, quantity: 20 }));
  };
  const price120FromRows = (rows) => {
    const total = (rows || []).reduce((s, r) => s + (Number(itemsById[r.item_id]?.price || 0) * Number(r.quantity || 0)), 0);
    return Math.round(total * 100) / 100;
  };
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState({});
  // Inline "Neuen Kunden anlegen" – kompaktes Formular direkt im Bestell-Dialog
  const [newCust, setNewCust] = useState(null); // null = zu, {} = offen
  const [savingCust, setSavingCust] = useState(false);
  const [custPrices, setCustPrices] = useState({});      // item_id -> Kundenpreis (Neu-Dialog)
  const [editCustPrices, setEditCustPrices] = useState({}); // item_id -> Kundenpreis (Bearbeiten)
  const [packaging, setPackaging] = useState([]);        // Verpackungs-Artikel (13er/40er)
  const [packMap, setPackMap] = useState({});            // Warengruppen→40er / 13er / Karton
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [sendingConf, setSendingConf] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [custAddresses, setCustAddresses] = useState([]);
  const [deliveryAddr, setDeliveryAddr] = useState(null);
  const [deliveryAddrId, setDeliveryAddrId] = useState("main");
  const [newAddr, setNewAddr] = useState(null);
  const [savingAddr, setSavingAddr] = useState(false);
  const [whRows, setWhRows] = useState([]);
  const [whLoading, setWhLoading] = useState(false);
  const [whStatus, setWhStatus] = useState("all");
  const { user } = useAuth();
  const isWarehouse = user?.role === "warehouse";
  // Ansicht: Liste vs. Kalender (Wunschtermine)
  const [view, setView] = useState("list");
  // Lagermitarbeiter sehen nur die Lager-Ansicht
  useEffect(() => { if (isWarehouse) setView("warehouse"); }, [isWarehouse]);
  const [calMode, setCalMode] = useState("month");
  const [calAnchor, setCalAnchor] = useState(new Date());
  const [calRows, setCalRows] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const segCls = (a) => `rounded-full px-3 h-7 text-xs font-bold transition-colors ${a ? "bg-[var(--pop-pink)] text-white" : "text-[var(--text-muted)] hover:text-[var(--text)]"}`;

  const load = async () => {
    const r = await api.get("/orders", { params: { page, page_size: pageSize } });
    setRows(r.data);
    setTotal(Number(r.headers["x-total-count"]) || r.data.length);
  };
  useEffect(() => {
    load();
  }, [page]);
  useEffect(() => {
    if (view !== "calendar") return;
    setCalLoading(true);
    api.get("/orders", { params: { page: 1, page_size: 1000 } })
      .then((r) => {
        setCalRows(r.data);
        // Auto-Fokus: Monat des nächsten anstehenden Wunschtermins
        const todayKey = cymd(new Date());
        const future = r.data
          .map((o) => (o.expected_delivery || "").slice(0, 10))
          .filter((k) => k && k >= todayKey)
          .sort();
        if (future.length) {
          const [y, m, d] = future[0].split("-").map(Number);
          setCalAnchor(new Date(y, m - 1, d));
        }
      })
      .catch(() => setCalRows([]))
      .finally(() => setCalLoading(false));
  }, [view]);

  const loadWh = () => {
    setWhLoading(true);
    api.get("/orders", { params: { page: 1, page_size: 1000 } })
      .then((r) => setWhRows(r.data.filter((o) => o.warehouse_released !== false)))
      .catch(() => setWhRows([]))
      .finally(() => setWhLoading(false));
  };
  useEffect(() => { if (view === "warehouse") loadWh(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/items").then((r) => setItems(r.data.filter((i) => i.type === "finished")));
    api.get("/items?type=packaging&page_size=500").then((r) => setPackaging(r.data)).catch(() => {});
    api.get("/b2b-prices").then((r) => setB2bMap(Object.fromEntries((r.data || []).map((x) => [x.item_id, x])))).catch(() => {});
    api.get("/offers/active").then((r) => setOffers(r.data || [])).catch(() => {});
    api.get("/settings/packaging-map").then((r) => setPackMap(r.data || {})).catch(() => {});
  }, []);

  // Kundenspezifische Preise laden → { item_id: preis }
  const loadCustPrices = async (customerId) => {
    if (!customerId) return {};
    try {
      const { data } = await api.get(`/customers/${customerId}/prices`);
      const map = {};
      (data || []).forEach((p) => { if (p.item_id != null && p.price != null) map[p.item_id] = p.price; });
      return map;
    } catch { return {}; }
  };

  // Bundle-Einheiten (Fallback, falls am Artikel nicht gepflegt)
  const BUNDLE_UNITS = { "13er": 13, "40er": 40, "120er": 120, "palette": 520 };
  const unitsFor = (itemObj, bundleCode) =>
    Number(itemObj?.bundles?.[bundleCode]?.units) || BUNDLE_UNITS[bundleCode] || 1;

  // Kundenpreis-Dokument (Einzel + Bündel) laden
  const fetchCustPriceDoc = async (customerId, itemId) => {
    if (!customerId || !itemId) return null;
    try { const { data } = await api.get(`/customers/${customerId}/prices/${itemId}`); return data; }
    catch (_e) { return null; }
  };

  const offerLabel = (o) => {
    if (!o) return null;
    if (o.discount_type === "percent") return `Aktion -${o.discount_value}%`;
    return `Aktion -${Number(o.discount_value).toFixed(2).replace(".", ",")} EUR/Einheit`;
  };

  // Aktions-Rabatt (nur B2B); größte Ersparnis gewinnt. Liefert { price, offer }.
  const applyOfferDetailed = (price, itemObj, bundleCode) => {
    const round2 = (x) => Math.round(Number((x * 100).toPrecision(12))) / 100;
    const units = { "13er": 13, "40er": 40, "120er": 120, "palette": 520 }[bundleCode] || 1;
    let best = price, bestO = null;
    for (const o of offers) {
      const inScope = (o.item_ids || []).includes(itemObj?.id) || (itemObj?.product_group && (o.group_names || []).includes(itemObj.product_group));
      if (!inScope) continue;
      const val = Number(o.discount_value || 0);
      if (val <= 0) continue;
      const p = o.discount_type === "percent" ? round2(price * (1 - val / 100)) : round2(Math.max(0, price - val * units));
      if (p < best) { best = p; bestO = o; }
    }
    return { price: best, offer: bestO };
  };

  // Positionspreis + Aktions-Label: Kundenpreis > B2B-Matrix > Einzelpreis × Einheiten, dann Aktionsrabatt (B2B).
  const resolveLine = (doc, itemObj, bundleCode, custType) => {
    let resolved = null;
    if (!bundleCode) {
      if (doc && doc.price != null) resolved = Number(doc.price);
    } else {
      const cb = doc && doc.bundle_prices ? doc.bundle_prices[bundleCode] : null;
      if (cb != null && Number(cb) > 0) resolved = Number(cb);
    }
    if (resolved == null && custType === "B2B") {
      const m = b2bMap[itemObj?.id];
      if (m) {
        if (!bundleCode && m.price != null) resolved = Number(m.price);
        if (bundleCode && m.bundle_prices && m.bundle_prices[bundleCode] != null) resolved = Number(m.bundle_prices[bundleCode]);
      }
    }
    if (resolved == null) {
      const base = Number(itemObj?.price || 0);
      resolved = bundleCode ? Math.round(base * unitsFor(itemObj, bundleCode) * 100) / 100 : base;
    }
    let offer_label = null; let list_price = null;
    if (custType === "B2B") {
      const r = applyOfferDetailed(resolved, itemObj, bundleCode);
      if (r.offer) { list_price = resolved; resolved = r.price; offer_label = offerLabel(r.offer); }
    }
    return { price: resolved, offer_label, list_price };
  };

  // Alle Positionen neu bepreisen (z. B. nach Kundenwechsel)
  const repriceLines = async (lns, custId, custType) => {
    const out = [];
    for (const l of lns) {
      if (!l.item_id || pkgById[l.item_id]) { out.push(l); continue; }
      if (l.bundle_code === "120er") { out.push({ ...l, price: String(price120FromRows(l.display_rows)) }); continue; }
      const doc = await fetchCustPriceDoc(custId, l.item_id);
      const r = resolveLine(doc, itemsById[l.item_id], l.bundle_code || "", custType);
      out.push({ ...l, price: String(r.price), offer_label: r.offer_label, list_price: r.list_price });
    }
    return out;
  };

  // Einzelne Position ohne Aktion/Manuell neu bepreisen (Fallback beim Entfernen des manuellen Rabatts)
  const repriceOne = async (l, custId, custType) => {
    if (l.bundle_code === "120er") {
      return { price: price120FromRows(l.display_rows || buildRows120(l.item_id)), offer_label: null, list_price: null };
    }
    const doc = await fetchCustPriceDoc(custId, l.item_id);
    return resolveLine(doc, itemsById[l.item_id], l.bundle_code || "", custType);
  };
  // Manueller Positionsrabatt (%) – ersetzt den Aktionsrabatt für diese Zeile (entweder/oder)
  const setLineDiscount = async (idx, pctStr, lns, setLns, custId, custType) => {
    const c = [...lns]; const l = { ...c[idx] };
    l.manual_discount_pct = pctStr;
    const pct = Number(pctStr) || 0;
    if (pct > 0) {
      const base = l.list_price != null ? Number(l.list_price) : Number(l.price || 0);
      l.list_price = base;
      l.price = String(round2(base * (1 - pct / 100)));
      l.offer_label = `Rabatt -${pct}%`;
    } else {
      const r = await repriceOne(l, custId, custType);
      l.price = String(r.price); l.offer_label = r.offer_label; l.list_price = r.list_price;
      l.manual_discount_pct = null;
    }
    c[idx] = l; setLns(c);
  };

  // Verpackungs-Optionen: bevorzugt die definierten 13er/40er-SKUs, sonst alle Verpackungen
  const PKG_SKUS = ["VER-BOX-13", "VER-BOX-SEA-40", "VER-BOX-CB-40", "VER-BOX-COF-40", "VER-BOX-CC-40", "VER-BOX-CAR-40", "VER-BOX-TRU-40"];
  const pkgById = useMemo(() => Object.fromEntries(packaging.map((i) => [i.id, i])), [packaging]);
  const pkgOptions = useMemo(() => {
    const bySku = Object.fromEntries(packaging.map((i) => [i.sku, i]));
    const pref = PKG_SKUS.map((s) => bySku[s]).filter(Boolean);
    return pref.length ? pref : packaging;
  }, [packaging]);
  const addPkgLine = (id) => { if (pkgById[id]) setLines((c) => [...c, { item_id: id, quantity: "1", price: "0" }]); };
  const addPkgLineEdit = (id) => { if (pkgById[id]) setEditLines((c) => [...c, { item_id: id, quantity: "1", price: "0" }]); };

  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  // Auto-Verpackung: berechnet je Sorte die passende Verpackung aus den Tütenmengen
  const autoPack = (ls, setter, kind) => {
    const fl = ls.filter((l) => l.item_id && !pkgById[l.item_id] && Number(l.quantity) > 0)
                 .map((l) => ({ item: itemsById[l.item_id], bags: Number(l.quantity) }));
    if (!fl.length) { toast.error("Erst Fertigware-Positionen mit Menge erfassen"); return; }
    const agg = {}; const missing = [];
    if (kind === "40er") {
      const map = packMap.per_group_40er || {};
      fl.forEach(({ item, bags }) => {
        const pid = map[item?.product_group];
        if (!pid) { missing.push(item?.name || "?"); return; }
        agg[pid] = (agg[pid] || 0) + Math.ceil(bags / 40);
      });
    } else if (kind === "13er") {
      const pid = packMap.box13_item_id;
      if (!pid) { toast.error("13er-Verpackung nicht konfiguriert (Einstellungen)"); return; }
      agg[pid] = Math.ceil(fl.reduce((s, x) => s + x.bags, 0) / 13);
    } else {
      const pid = packMap.carton_item_id; const per = Number(packMap.carton_bags || 0);
      if (!pid || per <= 0) { toast.error("Umkarton nicht konfiguriert (Einstellungen)"); return; }
      agg[pid] = Math.ceil(fl.reduce((s, x) => s + x.bags, 0) / per);
    }
    if (Object.keys(agg).length) {
      setter((cur) => {
        const next = [...cur];
        Object.entries(agg).forEach(([pid, qty]) => {
          const i = next.findIndex((l) => l.item_id === pid);
          if (i >= 0) next[i] = { ...next[i], quantity: String(Number(next[i].quantity || 0) + qty) };
          else next.push({ item_id: pid, quantity: String(qty), price: "0" });
        });
        return next;
      });
      toast.success("Verpackung berechnet & hinzugefügt");
    }
    if (missing.length) toast.error(`Keine 40er-Verpackung für: ${[...new Set(missing)].join(", ")}`);
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        is_sample: !!form.is_sample,
        order_discount_pct: form.order_discount_pct ? Number(form.order_discount_pct) : null,
        order_discount_reason: form.order_discount_reason || null,
        delivery_address: deliveryAddr || null,
        lines: lines.filter((l) => l.item_id && Number(l.quantity) > 0)
                    .map((l) => ({
                      item_id: l.item_id, quantity: Number(l.quantity),
                      price: form.is_sample ? 0 : Number(l.price || 0),
                      bundle_code: l.bundle_code || null,
                      offer_label: form.is_sample ? null : (l.offer_label || null),
                      list_price: form.is_sample ? null : (l.list_price || null),
                      display_rows: l.bundle_code === "120er" ? (l.display_rows || buildRows120(l.item_id)).map((r) => ({ item_id: r.item_id, quantity: 20 })) : null,
                    })),
      };
      if (payload.lines.length === 0) return toast.error("Mindestens 1 Position nötig");
      await api.post("/orders", payload);
      toast.success(payload.is_sample ? "Probe-Bestellung angelegt" : "Bestellung angelegt");
      setOpen(false); setForm({ customer_id: "", order_date: "", expected_delivery: "", notes: "", is_sample: false, order_discount_pct: "", order_discount_reason: "" });
      setLines([{ item_id: "", quantity: "", price: "" }]);
      setCustAddresses([]); setDeliveryAddr(null); setDeliveryAddrId("main");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}`, { status }); load();
      if (detail?.id === id) setDetail((await api.get(`/orders/${id}`)).data);
    } catch (_e) { toast.error("Fehler"); }
  };

  const releaseToWarehouse = async (id) => {
    try {
      await api.post(`/orders/${id}/release-to-warehouse`);
      toast.success("Für Lager bestätigt");
      load();
      if (detail?.id === id) setDetail((await api.get(`/orders/${id}`)).data);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const addrLabel = (a) => [a.label, a.name, a.street, [a.zip, a.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
  const onPickDeliveryAddr = (v) => {
    if (!v) return; // Radix-Select feuert "" wenn neue Option noch nicht registriert ist
    setDeliveryAddrId(v);
    if (v === "main") { setDeliveryAddr(null); return; }
    const a = custAddresses.find((x) => x.id === v);
    setDeliveryAddr(a ? { name: a.name, street: a.street, zip: a.zip, city: a.city, country: a.country } : null);
  };
  const loadCustAddresses = async (id) => {
    setDeliveryAddr(null); setDeliveryAddrId("main"); setCustAddresses([]); setSelCustType(null);
    if (!id) return null;
    try { const { data } = await api.get(`/customers/${id}`); setCustAddresses(data.delivery_addresses || []); setSelCustType(data.type || null); return data; } catch (_e) { return null; }
  };
  const saveNewAddr = async () => {
    if (!form.customer_id) { toast.error("Erst Kunde wählen"); return; }
    if (!newAddr?.street && !newAddr?.city) { toast.error("Straße oder Ort nötig"); return; }
    setSavingAddr(true);
    try {
      const { data } = await api.post(`/customers/${form.customer_id}/addresses`, newAddr);
      const list = data.delivery_addresses || [];
      setCustAddresses(list);
      const added = list[list.length - 1];
      if (added) { setDeliveryAddrId(added.id); setDeliveryAddr({ name: added.name, street: added.street, zip: added.zip, city: added.city, country: added.country }); }
      setNewAddr(null);
      toast.success("Lieferadresse gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); } finally { setSavingAddr(false); }
  };

  const toDelivery = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/to-delivery`);
      toast.success(`Lieferschein ${data.delivery_note_number} erzeugt`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };
  const toggleAll = () => {
    const eligible = filtered.filter((r) => r.status === "confirmed").map((r) => r.id);
    const allOn = eligible.every((id) => selected[id]);
    const next = { ...selected };
    eligible.forEach((id) => { next[id] = !allOn; });
    setSelected(next);
  };
  const bulkToDelivery = async () => {
    const eligible = rows.filter((r) => selectedIds.includes(r.id) && r.status === "confirmed");
    if (!eligible.length) { toast.error("Bitte bestätigte Bestellungen auswählen"); return; }
    if (!window.confirm(`${eligible.length} Bestellung(en) in Lieferscheine überführen?`)) return;
    let ok = 0, fail = 0;
    for (const r of eligible) {
      try {
        await api.post(`/orders/${r.id}/to-delivery`);
        ok++;
      } catch (_err) { fail++; }
    }
    toast.success(`${ok} Lieferschein(e) erzeugt${fail ? ` · ${fail} Fehler` : ""}`);
    setSelected({});
    load();
  };

  const openDetail = async (id) => setDetail((await api.get(`/orders/${id}`)).data);

  const sendConfirmation = async () => {
    if (!confirmOrder) return;
    setSendingConf(true);
    try {
      const { data } = await api.post(`/orders/${confirmOrder.id}/send-confirmation`);
      toast.success(`Auftragsbestätigung an ${data.to} gesendet`);
      setConfirmOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setSendingConf(false); }
  };
  const confirm = useConfirm();

  const openEditOrder = async (r) => {
    try {
      const { data } = await api.get(`/orders/${r.id}`);
      setEditing(data);
      setEditForm({
        customer_id: data.customer_id,
        order_date: (data.order_date || "").slice(0, 10),
        expected_delivery: (data.expected_delivery || "").slice(0, 10),
        status: data.status,
        notes: data.notes || "",
        order_discount_pct: data.order_discount_pct != null ? String(data.order_discount_pct) : "",
        order_discount_reason: data.order_discount_reason || "",
      });
      setEditLines((data.lines || []).map((l) => ({
        item_id: l.item_id, quantity: String(l.quantity ?? ""),
        price: String(l.price ?? ""), bundle_code: l.bundle_code || "",
        offer_label: l.offer_label || null,
        list_price: l.list_price || null,
        display_rows: l.display_rows || null,
      })));
      setEditCustPrices(await loadCustPrices(data.customer_id));
      setEditSelCustType((data.customer && data.customer.type) || null);
    } catch (err) { toast.error(err.response?.data?.detail || "Bestellung nicht geladen"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setEditSaving(true);
    try {
      const payload = {
        customer_id: editForm.customer_id,
        order_date: editForm.order_date || null,
        expected_delivery: editForm.expected_delivery || null,
        status: editForm.status,
        notes: editForm.notes || null,
        order_discount_pct: editForm.order_discount_pct ? Number(editForm.order_discount_pct) : null,
        order_discount_reason: editForm.order_discount_reason || null,
        lines: editLines.filter((l) => l.item_id && Number(l.quantity) > 0)
                        .map((l) => ({ item_id: l.item_id,
                                       quantity: Number(l.quantity),
                                       price: Number(l.price || 0), bundle_code: l.bundle_code || null,
                                       offer_label: l.offer_label || null,
                                       list_price: l.list_price || null,
                                       display_rows: l.bundle_code === "120er" ? (l.display_rows || buildRows120(l.item_id)).map((r) => ({ item_id: r.item_id, quantity: 20 })) : null })),
      };
      if (payload.lines.length === 0) return toast.error("Mindestens 1 Position nötig");
      await api.patch(`/orders/${editing.id}`, payload);
      toast.success(`Bestellung ${editing.number} aktualisiert`);
      setEditing(null); setEditForm(null); setEditLines([]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setEditSaving(false); }
  };

  const removeOrder = async (r) => {
    const ok = await confirm({
      title: `Bestellung ${r.number} löschen?`,
      description: "Bestellungen mit generiertem Lieferschein können nicht gelöscht werden.",
      confirmLabel: "Löschen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/orders/${r.id}`);
      toast.success(`Bestellung ${r.number} gelöscht`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Fehler"));
    }
  };

  const openNewCust = () => setNewCust({ name: "", email: "", phone: "", street: "", zip: "", city: "" });
  const saveNewCust = async () => {
    if (!newCust?.name?.trim()) return toast.error("Name ist Pflicht");
    setSavingCust(true);
    try {
      const { data } = await api.post("/customers", {
        name: newCust.name.trim(),
        email: newCust.email || null,
        phone: newCust.phone || null,
        street: newCust.street || null,
        zip: newCust.zip || null,
        city: newCust.city || null,
        state: newCust.state || null,
        type: "B2C",
        country: "DE",
      });
      // Optimistically add + auto-select
      setCustomers((c) => [...c, data]);
      setForm((f) => ({ ...f, customer_id: data.id }));
      loadCustAddresses(data.id);  // neuer Kunde hat (noch) keine Lieferadressen → zurücksetzen
      toast.success(`Kunde ${data.name} angelegt & zugewiesen`);
      setNewCust(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde konnte nicht angelegt werden");
    } finally { setSavingCust(false); }
  };

  const isRowShipped = (r) => ["shipped", "delivered"].includes(r.delivery_status) || ["in_transit", "delivered"].includes(r.dsv_status);

  const filtered = rows.filter((r) => {
    if (statusFilter === "shipped") { if (!isRowShipped(r)) return false; }
    else if (statusFilter === "ready") { if (r.status !== "ready" || isRowShipped(r)) return false; }
    else if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.number || "").toLowerCase().includes(s)
        || (r.customer_name || "").toLowerCase().includes(s)
        || (r.status || "").toLowerCase().includes(s);
  });

  const calFiltered = useMemo(() => calRows.filter((r) => {
    if (!r.expected_delivery) return false;
    if (statusFilter === "shipped") { if (!isRowShipped(r)) return false; }
    else if (statusFilter === "ready") { if (r.status !== "ready" || isRowShipped(r)) return false; }
    else if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.number || "").toLowerCase().includes(s)
        || (r.customer_name || "").toLowerCase().includes(s)
        || (r.status || "").toLowerCase().includes(s);
  }), [calRows, statusFilter, q]);

  const fmt = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

  return (
    <div data-testid="page-orders">
      <PageHeader
        eyebrow="Vertrieb"
        title="Kundenbestellungen"
        description="Eingehende Bestellungen erfassen, bestätigen und in Lieferscheine überführen."
        actions={
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button data-testid="btn-export-orders" variant="outline" className="rounded-full"
                    onClick={() => downloadCsv("/exports/orders.csv", "bestellungen.csv")}>
              <Download size={16} className="mr-1" /> CSV
            </Button>
            <Button data-testid="btn-import-order" variant="outline" className="rounded-full"
                    onClick={() => setImportOpen(true)}>
              <Sparkles size={16} className="mr-1" /> Importieren
            </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-order" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                <Plus size={16} className="mr-1" /> Neue Bestellung
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Neue Bestellung</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label>Kunde *</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <CustomerAutocomplete
                          value={form.customer_id}
                          onChange={async (id) => {
                            setForm((f) => ({ ...f, customer_id: id }));
                            const data = await loadCustAddresses(id);
                            const map = await loadCustPrices(id);
                            setCustPrices(map);
                            setLines(await repriceLines(lines, id, data?.type));
                          }}
                          customers={customers}
                          serverSearch={true}
                          testId="order-customer-autocomplete"
                        />
                      </div>
                      <button type="button" onClick={openNewCust}
                              data-testid="btn-inline-new-customer"
                              title="Neuen Kunden anlegen"
                              className="h-10 w-10 flex items-center justify-center rounded-lg bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] flex-shrink-0">
                        <UserPlus size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Bestelldatum</Label><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
                    <div><Label>Wunschlieferung</Label><Input type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} /></div>
                  </div>
                </div>
                {form.customer_id && (
                  <div data-testid="order-delivery-address">
                    <Label>Lieferadresse</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <Select value={deliveryAddrId} onValueChange={onPickDeliveryAddr}>
                          <SelectTrigger data-testid="order-delivery-address-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="main">Hauptadresse (Rechnungsadresse)</SelectItem>
                            {custAddresses.map((a) => <SelectItem key={a.id} value={a.id}>{addrLabel(a)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <button type="button" onClick={() => setNewAddr({ label: "", name: "", street: "", zip: "", city: "", state: "", country: "DE" })}
                              data-testid="btn-add-delivery-address" title="Neue Lieferadresse"
                              className="h-10 w-10 flex items-center justify-center rounded-lg bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] flex-shrink-0">
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                )}
                <div className="border-t pt-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
                    <Label className="overline">Positionen</Label>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <button type="button" data-testid="order-auto-40er" onClick={() => autoPack(lines, setLines, "40er")} className="rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">Auto 40er</button>
                      <button type="button" data-testid="order-auto-13er" onClick={() => autoPack(lines, setLines, "13er")} className="rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">Auto 13er</button>
                      <button type="button" data-testid="order-auto-carton" onClick={() => autoPack(lines, setLines, "carton")} className="rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">Auto Karton</button>
                      <Select value="none" onValueChange={(v) => { if (v !== "none") addPkgLine(v); }}>
                        <SelectTrigger data-testid="order-add-packaging" className="h-8 rounded-full text-xs w-auto gap-1"><SelectValue placeholder="+ Verpackung" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Verpackung wählen…</SelectItem>
                          {pkgOptions.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => setLines([...lines, { item_id: "", quantity: "", price: "" }])}
                        className="rounded-full"><Plus size={14} /> Zeile</Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {lines.map((l, idx) => {
                      const pkg = pkgById[l.item_id];
                      const hasCust = !pkg && l.item_id && custPrices[l.item_id] != null;
                      return (
                      <div key={idx}>
                      {pkg ? (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px_100px_40px] gap-2 items-center">
                        <div className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] px-3 h-9 text-sm min-w-0" data-testid={`order-pkg-line-${idx}`}>
                          <Package size={14} className="text-[var(--pop-pink)] shrink-0" />
                          <span className="truncate font-medium">{pkg.name}</span>
                          <span className="text-[10px] font-mono text-[var(--text-muted)] shrink-0 ml-auto">{pkg.sku}</span>
                        </div>
                        <Input type="number" step="any" placeholder="Menge" value={l.quantity}
                          onChange={(e) => { const c = [...lines]; c[idx].quantity = e.target.value; setLines(c); }} />
                        <Input type="number" value={l.price ?? "0"} disabled className="opacity-60" title="Verpackung wird zu 0 € berechnet" />
                        <button type="button" onClick={() => setLines(lines.filter((_, i) => i !== idx))} className="text-red-500 justify-self-end sm:justify-self-auto"><Trash2 size={16} /></button>
                      </div>
                      ) : (
                      <>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_72px_104px_100px_40px] gap-2 items-center">
                        <ItemAutocomplete
                          value={l.item_id}
                          items={items}
                          onChange={async (id, it) => {
                            const c = [...lines]; c[idx].item_id = id;
                            const doc = await fetchCustPriceDoc(form.customer_id, id);
                            const r = resolveLine(doc, it || itemsById[id], c[idx].bundle_code, selCustType);
                            c[idx].price = String(r.price); c[idx].offer_label = r.offer_label; c[idx].list_price = r.list_price;
                            setLines([...c]);
                          }}
                          testId={`item-autocomplete-line-${idx}`}
                        />
                        <Input type="number" step="any" placeholder="Menge" value={l.quantity}
                          onChange={(e) => { const c = [...lines]; c[idx].quantity = e.target.value; setLines(c); }} />
                        <Select value={l.bundle_code || "none"} onValueChange={async (v) => {
                          const c = [...lines]; const bcode = v === "none" ? "" : v; c[idx].bundle_code = bcode;
                          if (bcode === "120er") {
                            if (!(c[idx].display_rows && c[idx].display_rows.length)) c[idx].display_rows = buildRows120(c[idx].item_id);
                            c[idx].price = String(price120FromRows(c[idx].display_rows)); c[idx].offer_label = null; c[idx].list_price = null;
                          } else {
                            c[idx].display_rows = null;
                            const doc = await fetchCustPriceDoc(form.customer_id, c[idx].item_id);
                            const r = resolveLine(doc, itemsById[c[idx].item_id], bcode, selCustType);
                            c[idx].price = String(r.price); c[idx].offer_label = r.offer_label; c[idx].list_price = r.list_price;
                          }
                          setLines([...c]);
                        }}>
                          <SelectTrigger data-testid={`order-line-bundle-${idx}`} className="text-xs"><SelectValue placeholder="Einzel" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Einzel (stk)</SelectItem>
                            <SelectItem value="13er">13er</SelectItem>
                            <SelectItem value="40er">40er</SelectItem>
                            <SelectItem value="120er">120er Display</SelectItem>
                            <SelectItem value="palette">Palette</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input type="number" step="0.01" placeholder="Preis €" value={l.price}
                          data-testid={`order-line-price-${idx}`}
                          className={hasCust ? "border-[var(--pop-pink)]" : ""}
                          onChange={(e) => { const c = [...lines]; c[idx].price = e.target.value; setLines(c); }} />
                        <button type="button" onClick={() => setLines(lines.filter((_, i) => i !== idx))} className="text-red-500 justify-self-end sm:justify-self-auto"><Trash2 size={16} /></button>
                      </div>
                      {(hasCust || l.bundle_code) && <div className="text-[10px] font-semibold text-[var(--pop-pink)] mt-0.5 pl-1" data-testid={`order-line-custprice-${idx}`}>★ {l.bundle_code ? `${l.bundle_code}-Preis` : "Kundenpreis"} · {fmt(Number(l.price) || 0)}{l.bundle_code ? " / Bündel" : ""}</div>}
                      {l.offer_label && <div className="mt-0.5 pl-1" data-testid={`order-line-offer-${idx}`}><span className="inline-block px-1.5 py-0.5 rounded-full bg-[var(--pop-yellow)] text-black text-[10px] font-bold">{l.offer_label}</span></div>}
                      {l.bundle_code === "120er" && (
                        <div className="mt-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] p-2 space-y-1" data-testid={`order-display120-${idx}`}>
                          <div className="text-[11px] font-semibold text-[var(--text-muted)] mb-1">120er-Display · 6 Reihen × 20 Stk</div>
                          {(l.display_rows || buildRows120(l.item_id)).map((r, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <span className="text-[11px] w-14 shrink-0 text-[var(--text-muted)]">Reihe {j + 1}</span>
                              <div className="flex-1 min-w-0">
                                <ItemAutocomplete value={r.item_id} items={items}
                                  onChange={(id) => { const c = [...lines]; const rows = [...(c[idx].display_rows || buildRows120(c[idx].item_id))]; rows[j] = { item_id: id, quantity: 20 }; c[idx].display_rows = rows; c[idx].price = String(price120FromRows(rows)); setLines(c); }}
                                  testId={`order-display120-${idx}-row-${j}`} />
                              </div>
                              <span className="text-[11px] text-[var(--text-muted)] shrink-0">20 Stk</span>
                            </div>
                          ))}
                        </div>
                      )}
                      </>
                      )}
                      </div>
                      );
                    })}
                  </div>
                </div>
                <div><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                {!form.is_sample && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]">
                    <div>
                      <Label>Auftragsrabatt (%)</Label>
                      <Input type="number" min="0" max="100" step="0.5" data-testid="order-discount-pct"
                             value={form.order_discount_pct}
                             onChange={(e) => setForm({ ...form, order_discount_pct: e.target.value })}
                             placeholder="z. B. 10" />
                      <div className="text-xs text-[var(--text-muted)] mt-1">Manueller Rabatt auf die Auftragssumme</div>
                    </div>
                    <div>
                      <Label>Rabattgrund (optional)</Label>
                      <Input data-testid="order-discount-reason"
                             value={form.order_discount_reason}
                             onChange={(e) => setForm({ ...form, order_discount_reason: e.target.value })}
                             placeholder="z. B. Treuerabatt" />
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="checkbox-order-sample"
                    checked={form.is_sample}
                    onChange={(e) => setForm({ ...form, is_sample: e.target.checked })}
                    className="h-4 w-4 accent-[var(--pop-pink)]"
                  />
                  <div>
                    <div className="text-sm font-semibold">🎁 Probe-Bestellung (kostenlos verschenken)</div>
                    <div className="text-xs text-[var(--text-muted)]">Preise werden auf 0 € gesetzt, keine Rechnung erzeugt</div>
                  </div>
                </label>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-order" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Inline: Neuer Kunde – kompakt, überlagert den Bestell-Dialog */}
          <Dialog open={!!newCust} onOpenChange={(o) => !o && setNewCust(null)}>
            <DialogContent className="max-w-md" data-testid="dialog-inline-customer">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2">
                  <UserPlus size={20} className="text-[var(--pop-pink)]" /> Neuer Kunde
                </DialogTitle>
              </DialogHeader>
              {newCust && (
                <div className="space-y-3">
                  <div><Label>Name *</Label>
                    <Input required autoFocus data-testid="input-new-cust-name"
                           value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>E-Mail</Label>
                      <Input type="email" data-testid="input-new-cust-email"
                             value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} /></div>
                    <div><Label>Telefon</Label>
                      <Input data-testid="input-new-cust-phone"
                             value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} /></div>
                  </div>
                  <div><Label>Straße</Label>
                    <Input data-testid="input-new-cust-street"
                           value={newCust.street} onChange={(e) => setNewCust({ ...newCust, street: e.target.value })} /></div>
                  <div className="grid grid-cols-2 sm:grid-cols-[120px_1fr] gap-3">
                    <div><Label>PLZ</Label>
                      <Input data-testid="input-new-cust-zip"
                             value={newCust.zip}
                             onChange={(e) => { const v = e.target.value; setNewCust((c) => ({ ...c, zip: v })); if (/^\d{5}$/.test(v)) lookupPlz(v).then((r) => r && setNewCust((c) => ({ ...c, city: r.city, state: r.state, country: r.country || "DE" }))); }} /></div>
                    <div><Label>Ort</Label>
                      <Input data-testid="input-new-cust-city"
                             value={newCust.city} onChange={(e) => setNewCust({ ...newCust, city: e.target.value })} /></div>
                  </div>
                  <div><Label>Bundesland</Label>
                    <Input data-testid="input-new-cust-state"
                           value={newCust.state || ""} onChange={(e) => setNewCust({ ...newCust, state: e.target.value })} /></div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setNewCust(null)} className="rounded-full">Abbrechen</Button>
                    <Button type="button" onClick={saveNewCust} disabled={savingCust}
                            data-testid="btn-save-inline-customer"
                            className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                      {savingCust ? "Speichere…" : "Anlegen & zuweisen"}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={!!newAddr} onOpenChange={(o) => !o && setNewAddr(null)}>
            <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto" data-testid="dialog-new-address">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2">
                  <MapPin size={20} className="text-[var(--pop-pink)]" /> Neue Lieferadresse
                </DialogTitle>
              </DialogHeader>
              {newAddr && (
                <div className="space-y-3">
                  <div><Label>Bezeichnung (optional)</Label>
                    <Input data-testid="addr-label" placeholder="z. B. Lager Nord"
                           value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} /></div>
                  <div><Label>Name / Firma</Label>
                    <Input data-testid="addr-name" value={newAddr.name} onChange={(e) => setNewAddr({ ...newAddr, name: e.target.value })} /></div>
                  <div><Label>Straße</Label>
                    <Input data-testid="addr-street" value={newAddr.street} onChange={(e) => setNewAddr({ ...newAddr, street: e.target.value })} /></div>
                  <div className="grid grid-cols-2 sm:grid-cols-[120px_1fr] gap-3">
                    <div><Label>PLZ</Label><Input data-testid="addr-zip" value={newAddr.zip} onChange={(e) => { const v = e.target.value; setNewAddr((a) => ({ ...a, zip: v })); if (/^\d{5}$/.test(v)) lookupPlz(v).then((r) => r && setNewAddr((a) => ({ ...a, city: r.city, state: r.state, country: r.country || "DE" }))); }} /></div>
                    <div><Label>Ort</Label><Input data-testid="addr-city" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} /></div>
                  </div>
                  <div><Label>Bundesland</Label><Input data-testid="addr-state" value={newAddr.state || ""} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} /></div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setNewAddr(null)} className="rounded-full">Abbrechen</Button>
                    <Button type="button" onClick={saveNewAddr} disabled={savingAddr} data-testid="btn-save-address"
                            className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                      {savingAddr ? "Speichere…" : "Speichern"}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <OrderImportDialog open={importOpen} onClose={() => setImportOpen(false)} customers={customers} items={items}
                             onCreated={() => { setImportOpen(false); load(); }} />
          </div>
        }
      />

      <div className={view === "warehouse" ? "block" : "grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start"}>
        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden w-full">
          <div className="p-3 border-b border-[var(--border)] space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <Input placeholder="Suche Nr., Kunde, Status…" value={q} onChange={(e) => setQ(e.target.value)}
                       className="pl-8 rounded-xl h-9 w-full" data-testid="input-orders-search" />
              </div>
              <div className="text-xs text-[var(--text-muted)] shrink-0">{filtered.length}/{rows.length}</div>
            </div>
            <div className="flex items-center gap-1.5" data-testid="orders-view-toggle">
              <div className="inline-flex rounded-full border border-[var(--border)] p-0.5">
                {!isWarehouse && (
                <button type="button" data-testid="orders-view-list" onClick={() => setView("list")} className={segCls(view === "list")}>
                  <span className="inline-flex items-center gap-1"><List size={13} /> Liste</span>
                </button>
                )}
                {!isWarehouse && (
                <button type="button" data-testid="orders-view-calendar" onClick={() => setView("calendar")} className={segCls(view === "calendar")}>
                  <span className="inline-flex items-center gap-1"><CalendarDays size={13} /> Kalender</span>
                </button>
                )}
                <button type="button" data-testid="orders-view-warehouse" onClick={() => setView("warehouse")} className={segCls(view === "warehouse")}>
                  <span className="inline-flex items-center gap-1"><Boxes size={13} /> Lager</span>
                </button>
              </div>
              {view === "calendar" && (
                <div className="inline-flex rounded-full border border-[var(--border)] p-0.5">
                  <button type="button" data-testid="orders-cal-month" onClick={() => setCalMode("month")} className={segCls(calMode === "month")}>Monat</button>
                  <button type="button" data-testid="orders-cal-week" onClick={() => setCalMode("week")} className={segCls(calMode === "week")}>Woche</button>
                </div>
              )}
            </div>
            {view !== "warehouse" && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="orders-status-filters">
              {[
                { v: "all", label: "Alle" },
                { v: "draft", label: "Entwurf" },
                { v: "confirmed", label: "Bestätigt" },
                { v: "in_production", label: "In Produktion" },
                { v: "ready", label: "Versandbereit" },
                { v: "shipped", label: "Versendet" },
                { v: "cancelled", label: "Storniert" },
              ].map((s) => (
                <button key={s.v} type="button" data-testid={`orders-filter-${s.v}`}
                        onClick={() => setStatusFilter(s.v)}
                        className={`rounded-full px-3 h-7 text-xs font-semibold border transition-colors ${
                          statusFilter === s.v
                            ? "bg-[var(--pop-pink)] text-white border-[var(--pop-pink)]"
                            : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]"
                        }`}>
                  {s.label}
                </button>
              ))}
            </div>
            )}
          </div>
          {view === "list" ? (<>
          {selectedIds.length > 0 && (
            <div className="p-3 border-b border-[var(--border)] bg-[var(--pop-cream)]/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-sm">
                <b>{selectedIds.length}</b> Bestellung(en) ausgewählt
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setSelected({})} className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]">
                  Auswahl aufheben
                </button>
                <button onClick={bulkToDelivery} data-testid="btn-bulk-to-delivery"
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--success)] text-white px-4 py-1.5 text-sm font-bold hover:opacity-90">
                  <TruckIcon size={14} /> Als Lieferschein
                </button>
              </div>
            </div>
          )}
          {/* Einheitliche Karten-/Listenansicht (Desktop + Mobile) */}
          <div className="p-3 space-y-2" data-testid="orders-list">
            {filtered.length > 0 && (
              <div className="flex items-center gap-2 px-1 pb-1">
                <button onClick={toggleAll} data-testid="orders-select-all"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--pop-pink)]">
                  {(() => {
                    const eligible = filtered.filter((r) => r.status === "confirmed");
                    const allOn = eligible.length > 0 && eligible.every((r) => selected[r.id]);
                    return allOn ? <CheckSquare size={14} className="text-[var(--pop-pink)]" /> : <Square size={14} />;
                  })()}
                  Alle bestätigten auswählen
                </button>
              </div>
            )}
            {filtered.map((r) => {
              const selectable = r.status === "confirmed";
              const shipped = ["shipped", "delivered"].includes(r.delivery_status)
                || ["in_transit", "delivered"].includes(r.dsv_status);
              const completed = r.payment_status === "paid";
              return (
              <div key={r.id} data-testid={`order-card-${r.number}`}
                   onClick={() => openDetail(r.id)}
                   className={`group rounded-xl border p-4 cursor-pointer transition-colors ${
                     selected[r.id]
                       ? "border-[var(--pop-pink)] bg-[var(--surface-alt)]"
                       : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--pop-pink)]/50"
                   }`}>
                <div className="flex items-start gap-3">
                  <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    {selectable ? (
                      <button onClick={(e) => toggleSelect(r.id, e)} data-testid={`select-${r.number}`} className="hover:opacity-70">
                        {selected[r.id] ? <CheckSquare size={18} className="text-[var(--pop-pink)]" /> : <Square size={18} className="text-[var(--text-muted)]" />}
                      </button>
                    ) : (
                      <Square size={18} className="text-[var(--border)] opacity-40" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-bold text-base truncate min-w-0">{r.customer_name || "—"}</div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {completed && (
                          <span data-testid={`order-completed-${r.number}`}
                                title={r.payment_method_title || r.payment_method || "Bezahlt"}
                                className="pop-pill" style={{ background: "#d1fae5", color: "#065f46", fontSize: "10px" }}>
                            ✓ {shipped ? "VERSENDET & ABGESCHLOSSEN" : "ABGESCHLOSSEN"}
                          </span>
                        )}
                        {shipped ? (
                          <span data-testid={`order-shipped-${r.number}`} className="pop-pill" style={{ background: "#e2e8f0", color: "#334155", fontSize: "10px" }}>VERSENDET</span>
                        ) : (
                          <StatusBadge status={r.status} />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap min-w-0 mt-0.5">
                      <span className="font-mono text-sm text-[var(--text-muted)]">{r.number}</span>
                      {r.is_sample && <span data-testid={`sample-${r.number}`} className="pop-pill" style={{ background: "#fce7f3", color: "#9d174d", fontSize: "10px" }}>🎁 PROBE</span>}
                      {r.source === "portal" && <span data-testid={`portal-src-${r.number}`} className="pop-pill" style={{ background: "#ede9fe", color: "#5b21b6", fontSize: "10px" }}>🛒 PORTAL</span>}
                      {r.assigned_production_batch && (
                        <span data-testid={`assigned-batch-${r.number}`}
                              title={`Charge ${r.assigned_production_batch.charge_number} · Produziert am ${(r.assigned_production_batch.production_date || "").slice(0,10)} · MHD ${r.assigned_production_batch.mhd || "—"}`}
                              className="pop-pill inline-flex items-center gap-0.5" style={{ background: "#fce7f3", color: "#9d174d", fontSize: "10px" }}>
                          🏭 {r.assigned_production_batch.charge_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap mt-2">
                      <div className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] px-2.5 py-1">
                        <CalendarClock size={15} className="text-[var(--text)]" />
                        <span className="text-[11px] uppercase tracking-wide text-[var(--text-muted)]">Wunschtermin</span>
                        <span className="text-sm font-bold font-mono text-[var(--text)]">{r.expected_delivery || "—"}</span>
                      </div>
                      <span className="text-xs text-[var(--text-muted)] font-mono">Netto {fmt(r.total_net)}</span>
                    </div>
                    {r.dsv_tracking_number && (
                      <div className="mt-1.5" data-testid={`order-dsv-${r.number}`} title={`DSV ${r.dsv_tracking_number} · ${r.dsv_status_label || r.dsv_status || "gebucht"}`}>
                        <span className={`pop-pill text-[10px] font-bold inline-flex items-center gap-1 ${
                          r.dsv_status === "delivered" ? "bg-green-100 text-green-700"
                          : r.dsv_status === "in_transit" ? "bg-blue-100 text-blue-700"
                          : "bg-[#003580]/10 text-[#003580]"
                        }`}><TruckIcon size={10} /> DSV {r.dsv_status || "gebucht"}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                      {r.status === "draft" && (
                        <button data-testid={`btn-confirm-${r.number}`} onClick={() => updateStatus(r.id, "confirmed")}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--info)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          Bestätigen
                        </button>
                      )}
                      {r.status === "confirmed" && (
                        <button data-testid={`btn-todeliv-${r.number}`} onClick={() => toDelivery(r.id)}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--success)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <TruckIcon size={12} /> → Lieferschein
                        </button>
                      )}
                      {r.warehouse_released === false && r.status !== "cancelled" && (
                        <button data-testid={`btn-release-wh-${r.number}`} onClick={() => releaseToWarehouse(r.id)}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-pink)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <Boxes size={12} /> Für Lager bestätigen
                        </button>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <button data-testid={`btn-order-confirm-${r.number}`}
                                onClick={() => setConfirmOrder({ id: r.id, number: r.number })}
                                title="Auftragsbestätigung (PDF / E-Mail)"
                                className={`h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--border)] hover:border-[var(--pop-pink)] ${r.confirmation_sent_at ? "text-[var(--pop-pink)]" : "text-[var(--text-muted)] hover:text-[var(--pop-pink)]"}`}>
                          <Mail size={14} />
                        </button>
                        <button data-testid={`btn-edit-order-${r.number}`} onClick={() => openEditOrder(r)} title="Bestellung bearbeiten"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--border)] text-[var(--pop-pink)] hover:border-[var(--pop-pink)]">
                          <Pencil size={14} />
                        </button>
                        <button data-testid={`btn-delete-order-${r.number}`} onClick={() => removeOrder(r)} title="Bestellung löschen"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:text-red-600 hover:border-red-300">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-sm text-[var(--text-muted)]" data-testid="orders-empty">
                {rows.length === 0 ? "Noch keine Bestellungen." : "Keine Bestellungen für diesen Filter."}
              </div>
            )}
          </div>

          <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} onPageSizeChange={(n)=>{setPage(1);setPageSize(n);}} storageKey="orders" testIdPrefix="orders-pager" />
          </>) : view === "calendar" ? (
            <OrdersCalendar orders={calFiltered} loading={calLoading} mode={calMode} anchor={calAnchor} setAnchor={setCalAnchor} onOpen={openDetail} fmt={fmt} />
          ) : (
            <WarehouseBoard orders={whRows} loading={whLoading} statusFilter={whStatus} setStatusFilter={setWhStatus} q={q} onReload={loadWh} fmt={fmt} />
          )}
        </div>

        {view !== "warehouse" && (
        <div className="hidden lg:block bg-white border border-[var(--border)] rounded-2xl p-6 lg:sticky lg:top-4 self-start lg:max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="order-detail">
          {detail ? (
            <>
              <div className="overline mb-1">Bestellung</div>
              <h2 className="font-display font-black text-2xl mb-1">{detail.number}</h2>
              <div className="text-sm text-[var(--text-muted)] mb-4">{detail.customer?.name}</div>
              {detail.warehouse_released === false && detail.status !== "cancelled" && (
                <button data-testid="btn-release-wh-detail" onClick={() => releaseToWarehouse(detail.id)}
                        className="w-full mb-4 rounded-full bg-[var(--pop-pink)] text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2">
                  <Boxes size={16} /> Für Lager bestätigen
                </button>
              )}
              {detail.delivery_address && (
                <div className="mb-4 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] p-3" data-testid="detail-delivery-address">
                  <div className="overline mb-1 flex items-center gap-1"><MapPin size={12} /> Lieferadresse</div>
                  <div className="text-sm">{[detail.delivery_address.name, detail.delivery_address.street, [detail.delivery_address.zip, detail.delivery_address.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
                </div>
              )}
              <div className="overline mb-2">Positionen</div>
              <ul className="divide-y divide-[var(--border)]">
                {detail.lines?.map((l, i) => (
                  <li key={i} className="py-2 flex justify-between">
                    <div>
                      <div className="text-sm font-medium">{l.item_name}</div>
                      <div className="text-xs font-mono text-[var(--text-muted)]">{l.item_sku}</div>
                      {l.offer_label && <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-[var(--pop-yellow)] text-black text-[10px] font-bold" data-testid={`detail-offer-${i}`}>{l.offer_label}</span>}
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{l.quantity} {l.item_unit}</div>
                      <div className="text-xs text-[var(--text-muted)]">{fmt((l.price || 0) * (l.quantity || 0))}</div>
                    </div>
                  </li>
                ))}
              </ul>
              {(() => {
                const sub = (detail.lines || []).reduce((s, l) => s + (l.price || 0) * (l.quantity || 0), 0);
                const odp = Number(detail.order_discount_pct || 0);
                const disc = odp > 0 ? sub * odp / 100 : 0;
                const ship = Number(detail.shipping_net || 0);
                const total = sub - disc + ship;
                return (
                  <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-1 text-sm" data-testid="detail-totals">
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">Zwischensumme (netto)</span><span className="font-mono">{fmt(sub)}</span></div>
                    {odp > 0 && <div className="flex justify-between text-[var(--pop-pink)]"><span>Auftragsrabatt -{odp}%{detail.order_discount_reason ? ` (${detail.order_discount_reason})` : ""}</span><span className="font-mono">-{fmt(disc)}</span></div>}
                    {ship > 0 && <div className="flex justify-between" data-testid="detail-shipping"><span className="text-[var(--text-muted)]">Versand (netto){detail.shipping_class ? ` · ${detail.shipping_class}` : ""}</span><span className="font-mono">{fmt(ship)}</span></div>}
                    <div className="flex justify-between font-bold pt-1 border-t border-[var(--border)]"><span>Gesamt (netto)</span><span className="font-mono" data-testid="detail-total">{fmt(total)}</span></div>
                    {ship > 0 && <div className="text-[11px] text-[var(--text-muted)]">Versand inkl. 19% MwSt.: {fmt(Number(detail.shipping_gross || ship * 1.19))}</div>}
                  </div>
                );
              })()}
              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <div className="overline mb-2">Auftragsbestätigung</div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-full flex-1" data-testid="btn-detail-confirm-pdf"
                          onClick={() => openPdf(`/orders/${detail.id}/confirmation.pdf`).catch(() => toast.error("PDF konnte nicht geöffnet werden"))}>
                    <FileText size={14} className="mr-1" /> PDF
                  </Button>
                  <Button className="rounded-full flex-1 bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                          data-testid="btn-detail-confirm-send"
                          onClick={() => setConfirmOrder({ id: detail.id, number: detail.number })}>
                    <Mail size={14} className="mr-1" /> Per E-Mail
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-[var(--text-muted)]">Wähle links eine Bestellung, um Details zu sehen.</div>
          )}
        </div>
        )}
      </div>

      {/* Auftragsbestätigung Dialog */}
      <Dialog open={!!confirmOrder} onOpenChange={(o) => !o && setConfirmOrder(null)}>
        <DialogContent data-testid="dialog-order-confirmation">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Auftragsbestätigung {confirmOrder?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--text-muted)]">
              Im Rechnungsdesign als PDF ansehen oder dem Kunden per E-Mail senden (inkl. PDF-Anhang). Logo, Absender & Texte stammen aus den Einstellungen.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1" data-testid="btn-confirm-view-pdf"
                      onClick={() => confirmOrder && openPdf(`/orders/${confirmOrder.id}/confirmation.pdf`).catch(() => toast.error("PDF konnte nicht geöffnet werden"))}>
                <FileText size={15} className="mr-1" /> PDF ansehen
              </Button>
              <Button className="rounded-full flex-1 bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                      disabled={sendingConf} data-testid="btn-confirm-send-email" onClick={sendConfirmation}>
                <Mail size={15} className="mr-1" /> {sendingConf ? "Sende…" : "Per E-Mail senden"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit-Order Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditForm(null); setEditLines([]); } }}>
        <DialogContent className="max-w-3xl" data-testid="dialog-edit-order">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Bestellung {editing?.number} bearbeiten
            </DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label>Kunde *</Label>
                  <CustomerAutocomplete
                    value={editForm.customer_id}
                    onChange={async (id) => {
                      setEditForm((f) => ({ ...f, customer_id: id }));
                      let type = null;
                      try { const { data } = await api.get(`/customers/${id}`); type = data.type || null; } catch (_e) { /* noop */ }
                      setEditSelCustType(type);
                      const map = await loadCustPrices(id);
                      setEditCustPrices(map);
                      setEditLines(await repriceLines(editLines, id, type));
                    }}
                    customers={customers} serverSearch={true}
                    testId="edit-order-customer-autocomplete"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Bestelldatum</Label>
                    <Input type="date" data-testid="edit-order-date"
                           value={editForm.order_date}
                           onChange={(e) => setEditForm({ ...editForm, order_date: e.target.value })} /></div>
                  <div><Label>Wunschlieferung</Label>
                    <Input type="date" data-testid="edit-order-expected"
                           value={editForm.expected_delivery}
                           onChange={(e) => setEditForm({ ...editForm, expected_delivery: e.target.value })} /></div>
                </div>
              </div>
              <div><Label>Status</Label>
                <Select value={editForm.status}
                        onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-order-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Entwurf</SelectItem>
                    <SelectItem value="confirmed">Bestätigt</SelectItem>
                    <SelectItem value="in_production">In Produktion</SelectItem>
                    <SelectItem value="ready">Versandbereit</SelectItem>
                    <SelectItem value="cancelled">Storniert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border-t pt-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
                  <Label className="overline">Positionen</Label>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <button type="button" data-testid="edit-auto-40er" onClick={() => autoPack(editLines, setEditLines, "40er")} className="rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">Auto 40er</button>
                    <button type="button" data-testid="edit-auto-13er" onClick={() => autoPack(editLines, setEditLines, "13er")} className="rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">Auto 13er</button>
                    <button type="button" data-testid="edit-auto-carton" onClick={() => autoPack(editLines, setEditLines, "carton")} className="rounded-full border border-[var(--border)] px-3 h-8 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">Auto Karton</button>
                    <Select value="none" onValueChange={(v) => { if (v !== "none") addPkgLineEdit(v); }}>
                      <SelectTrigger data-testid="edit-add-packaging" className="h-8 rounded-full text-xs w-auto gap-1"><SelectValue placeholder="+ Verpackung" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Verpackung wählen…</SelectItem>
                        {pkgOptions.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="outline"
                            onClick={() => setEditLines([...editLines, { item_id: "", quantity: "", price: "" }])}
                            className="rounded-full"><Plus size={14} /> Zeile</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {editLines.map((l, idx) => {
                    const pkg = pkgById[l.item_id];
                    const hasCust = !pkg && l.item_id && editCustPrices[l.item_id] != null;
                    return (
                    <div key={idx}>
                    {pkg ? (
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px_100px_40px] gap-2 items-center">
                      <div className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] px-3 h-9 text-sm min-w-0" data-testid={`edit-pkg-line-${idx}`}>
                        <Package size={14} className="text-[var(--pop-pink)] shrink-0" />
                        <span className="truncate font-medium">{pkg.name}</span>
                        <span className="text-[10px] font-mono text-[var(--text-muted)] shrink-0 ml-auto">{pkg.sku}</span>
                      </div>
                      <Input type="number" step="any" placeholder="Menge" value={l.quantity}
                             data-testid={`edit-qty-${idx}`}
                             onChange={(e) => { const c = [...editLines]; c[idx].quantity = e.target.value; setEditLines(c); }} />
                      <Input type="number" value={l.price ?? "0"} disabled className="opacity-60" title="Verpackung wird zu 0 € berechnet" />
                      <button type="button" onClick={() => setEditLines(editLines.filter((_, i) => i !== idx))} className="text-red-500 justify-self-end sm:justify-self-auto"><Trash2 size={16} /></button>
                    </div>
                    ) : (
                    <>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_72px_104px_100px_40px] gap-2 items-center">
                      <ItemAutocomplete value={l.item_id} items={items}
                                        onChange={async (id, it) => {
                                          const c = [...editLines]; c[idx].item_id = id;
                                          const doc = await fetchCustPriceDoc(editForm.customer_id, id);
                                          const r = resolveLine(doc, it || itemsById[id], c[idx].bundle_code, editSelCustType);
                                          c[idx].price = String(r.price); c[idx].offer_label = r.offer_label; c[idx].list_price = r.list_price;
                                          setEditLines([...c]);
                                        }}
                                        testId={`edit-item-autocomplete-line-${idx}`} />
                      <Input type="number" step="any" placeholder="Menge" value={l.quantity}
                             data-testid={`edit-qty-${idx}`}
                             onChange={(e) => { const c = [...editLines]; c[idx].quantity = e.target.value; setEditLines(c); }} />
                      <Select value={l.bundle_code || "none"} onValueChange={async (v) => {
                        const c = [...editLines]; const bcode = v === "none" ? "" : v; c[idx].bundle_code = bcode;
                        if (bcode === "120er") {
                          if (!(c[idx].display_rows && c[idx].display_rows.length)) c[idx].display_rows = buildRows120(c[idx].item_id);
                          c[idx].price = String(price120FromRows(c[idx].display_rows)); c[idx].offer_label = null; c[idx].list_price = null;
                        } else {
                          c[idx].display_rows = null;
                          const doc = await fetchCustPriceDoc(editForm.customer_id, c[idx].item_id);
                          const r = resolveLine(doc, itemsById[c[idx].item_id], bcode, editSelCustType);
                          c[idx].price = String(r.price); c[idx].offer_label = r.offer_label; c[idx].list_price = r.list_price;
                        }
                        setEditLines([...c]);
                      }}>
                        <SelectTrigger data-testid={`edit-line-bundle-${idx}`} className="text-xs"><SelectValue placeholder="Einzel" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Einzel (stk)</SelectItem>
                          <SelectItem value="13er">13er</SelectItem>
                          <SelectItem value="40er">40er</SelectItem>
                          <SelectItem value="120er">120er Display</SelectItem>
                          <SelectItem value="palette">Palette</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" step="0.01" placeholder="Preis €" value={l.price}
                             data-testid={`edit-line-price-${idx}`}
                             className={hasCust ? "border-[var(--pop-pink)]" : ""}
                             onChange={(e) => { const c = [...editLines]; c[idx].price = e.target.value; setEditLines(c); }} />
                      <button type="button" onClick={() => setEditLines(editLines.filter((_, i) => i !== idx))} className="text-red-500 justify-self-end sm:justify-self-auto"><Trash2 size={16} /></button>
                    </div>
                    {(hasCust || l.bundle_code) && <div className="text-[10px] font-semibold text-[var(--pop-pink)] mt-0.5 pl-1" data-testid={`edit-line-custprice-${idx}`}>★ {l.bundle_code ? `${l.bundle_code}-Preis` : "Kundenpreis"} · {fmt(Number(l.price) || 0)}{l.bundle_code ? " / Bündel" : ""}</div>}
                    {l.offer_label && <div className="mt-0.5 pl-1" data-testid={`edit-line-offer-${idx}`}><span className="inline-block px-1.5 py-0.5 rounded-full bg-[var(--pop-yellow)] text-black text-[10px] font-bold">{l.offer_label}</span></div>}
                    {l.bundle_code === "120er" && (
                      <div className="mt-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] p-2 space-y-1" data-testid={`edit-display120-${idx}`}>
                        <div className="text-[11px] font-semibold text-[var(--text-muted)] mb-1">120er-Display · 6 Reihen × 20 Stk</div>
                        {(l.display_rows || buildRows120(l.item_id)).map((r, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <span className="text-[11px] w-14 shrink-0 text-[var(--text-muted)]">Reihe {j + 1}</span>
                            <div className="flex-1 min-w-0">
                              <ItemAutocomplete value={r.item_id} items={items}
                                onChange={(id) => { const c = [...editLines]; const rows = [...(c[idx].display_rows || buildRows120(c[idx].item_id))]; rows[j] = { item_id: id, quantity: 20 }; c[idx].display_rows = rows; c[idx].price = String(price120FromRows(rows)); setEditLines(c); }}
                                testId={`edit-display120-${idx}-row-${j}`} />
                            </div>
                            <span className="text-[11px] text-[var(--text-muted)] shrink-0">20 Stk</span>
                          </div>
                        ))}
                      </div>
                    )}
                    </>
                    )}
                    </div>
                    );
                  })}
                </div>
              </div>
              <div><Label>Notizen</Label>
                <Input value={editForm.notes} data-testid="edit-order-notes"
                       onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]">
                <div>
                  <Label>Auftragsrabatt (%)</Label>
                  <Input type="number" min="0" max="100" step="0.5" data-testid="edit-order-discount-pct"
                         value={editForm.order_discount_pct}
                         onChange={(e) => setEditForm({ ...editForm, order_discount_pct: e.target.value })}
                         placeholder="z. B. 10" />
                  <div className="text-xs text-[var(--text-muted)] mt-1">Manueller Rabatt auf die Auftragssumme</div>
                </div>
                <div>
                  <Label>Rabattgrund (optional)</Label>
                  <Input data-testid="edit-order-discount-reason"
                         value={editForm.order_discount_reason}
                         onChange={(e) => setEditForm({ ...editForm, order_discount_reason: e.target.value })}
                         placeholder="z. B. Treuerabatt" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full">Abbrechen</Button>
                <Button type="submit" disabled={editSaving} data-testid="btn-save-edit-order"
                        className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                  {editSaving ? "Speichere…" : "Änderungen speichern"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile: Detail als Full-Screen-Modal */}      {detail && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
             data-testid="order-detail-modal-mobile"
             onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-y-auto p-6 pb-8 shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="overline">Bestellung</div>
                <h2 className="font-display font-black text-2xl">{detail.number}</h2>
                <div className="text-sm text-[var(--text-muted)]">{detail.customer?.name}</div>
              </div>
              <button onClick={() => setDetail(null)}
                      data-testid="btn-close-detail-mobile"
                      className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-[var(--pop-cream)]/60">
                <X size={20} />
              </button>
            </div>
            <div className="overline mb-2 mt-2">Positionen</div>
            <ul className="divide-y divide-[var(--border)]">
              {detail.lines?.map((l, i) => (
                <li key={i} className="py-3 flex justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{l.item_name}</div>
                    <div className="text-xs font-mono text-[var(--text-muted)]">{l.item_sku}</div>
                    {l.offer_label && <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-[var(--pop-yellow)] text-black text-[10px] font-bold" data-testid={`detail-offer-m-${i}`}>{l.offer_label}</span>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-sm">{l.quantity} {l.item_unit}</div>
                    <div className="text-xs text-[var(--text-muted)]">{fmt((l.price || 0) * (l.quantity || 0))}</div>
                  </div>
                </li>
              ))}
            </ul>
            {(() => {
              const sub = (detail.lines || []).reduce((s, l) => s + (l.price || 0) * (l.quantity || 0), 0);
              const odp = Number(detail.order_discount_pct || 0);
              const disc = odp > 0 ? sub * odp / 100 : 0;
              const ship = Number(detail.shipping_net || 0);
              const total = sub - disc + ship;
              return (
                <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-1 text-sm" data-testid="detail-totals-mobile">
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Zwischensumme (netto)</span><span className="font-mono">{fmt(sub)}</span></div>
                  {odp > 0 && <div className="flex justify-between text-[var(--pop-pink)]"><span>Auftragsrabatt -{odp}%{detail.order_discount_reason ? ` (${detail.order_discount_reason})` : ""}</span><span className="font-mono">-{fmt(disc)}</span></div>}
                  {ship > 0 && <div className="flex justify-between" data-testid="detail-shipping-mobile"><span className="text-[var(--text-muted)]">Versand (netto){detail.shipping_class ? ` · ${detail.shipping_class}` : ""}</span><span className="font-mono">{fmt(ship)}</span></div>}
                  <div className="flex justify-between font-bold pt-1 border-t border-[var(--border)]"><span>Gesamt (netto)</span><span className="font-mono">{fmt(total)}</span></div>
                  {ship > 0 && <div className="text-[11px] text-[var(--text-muted)]">Versand inkl. 19% MwSt.: {fmt(Number(detail.shipping_gross || ship * 1.19))}</div>}
                </div>
              );
            })()}
            <div className="mt-4 pt-4 border-t border-[var(--border)] flex flex-col gap-2">
              {detail.warehouse_released === false && detail.status !== "cancelled" && (
                <button data-testid="btn-release-wh-modal"
                        onClick={async () => { await releaseToWarehouse(detail.id); }}
                        className="w-full rounded-full bg-[var(--pop-pink)] text-white py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
                  <Boxes size={16} /> Für Lager bestätigen
                </button>
              )}
              {detail.status === "draft" && (
                <button data-testid="btn-confirm-modal"
                        onClick={async () => { await updateStatus(detail.id, "confirmed"); }}
                        className="w-full rounded-full bg-[var(--info)] text-white py-3 text-sm font-bold">
                  Bestätigen
                </button>
              )}
              {detail.status === "confirmed" && (
                <button data-testid="btn-todeliv-modal"
                        onClick={async () => { await toDelivery(detail.id); setDetail(null); }}
                        className="w-full rounded-full bg-[var(--success)] text-white py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
                  <TruckIcon size={16} /> → Lieferschein
                </button>
              )}
              <button onClick={() => setDetail(null)}
                      className="w-full rounded-full border border-[var(--border)] py-2.5 text-sm text-[var(--text-muted)]">
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
